import { createSlice, Dispatch, PayloadAction } from '@reduxjs/toolkit';
import { ApiError } from 'models/errors/ApiError';
import * as localStorage from 'utils/localStorageUtils';
import type { RootState } from 'store';
import { ProductRegistrationInfo } from 'models/productRegistration';

export type ProductRegistrationState = {
  productInfo: ProductRegistrationInfo | null;
  customerEmail: string | null;
  soldDate: number | null;
  hasErrors: boolean;
  shouldComplete: boolean;
  isCompleted: boolean;
};

export const defaultProductRegistrationState: ProductRegistrationState = {
  productInfo: null,
  customerEmail: null,
  soldDate: null,
  hasErrors: false,
  shouldComplete: false,
  isCompleted: false
};

// Export slice
export const productRegistrationSlice = createSlice({
  name: 'productRegistration',
  initialState: defaultProductRegistrationState,
  reducers: {
    resetProductRegistrationState: () => {
      localStorage.removeItem('productRegistration');
      return defaultProductRegistrationState;
    },
    setProductRegistrationInfo: (
      state,
      action: PayloadAction<ProductRegistrationInfo>
    ) => {
      state.productInfo = action.payload;
      state.isCompleted = false;
    },
    setCustomerEmail: (state, action: PayloadAction<string>) => {
      state.customerEmail = action.payload;
    },
    setSoldDate: (state, action: PayloadAction<number>) => {
      state.soldDate = action.payload;
    },
    completeProductRegistrationStart: (state) => {
      state.shouldComplete = true;
      state.hasErrors = false;
      localStorage.setItem('productRegistration', state.productInfo);
    },
    completeProductRegistrationSuccess: (state) => {
      state.isCompleted = true;
      state.shouldComplete = false;
      state.productInfo = null;
      state.soldDate = null;

      localStorage.removeItem('productRegistration');
    },
    completeProductRegistrationFailure: (state) => {
      state.hasErrors = true;
      state.isCompleted = false;
      state.productInfo = null;
      state.soldDate = null;

      localStorage.removeItem('productRegistration');
    },
    completeProductRegistrationCancel: (state) => {
      state.hasErrors = false;
      state.shouldComplete = false;
      state.isCompleted = false;
      state.productInfo = null;
      state.soldDate = null;

      localStorage.removeItem('productRegistration');
    }
  }
});

// Export actions
export const {
  resetProductRegistrationState,
  setProductRegistrationInfo,
  setCustomerEmail,
  setSoldDate,
  completeProductRegistrationStart,
  completeProductRegistrationSuccess,
  completeProductRegistrationFailure,
  completeProductRegistrationCancel
} = productRegistrationSlice.actions;

// Export reducer
export const productRegistrationReducer = productRegistrationSlice.reducer;

// Export selector
export const productRegistrationSelector = (state: RootState) =>
  state.productRegistration;

// Export thunks
export function checkProductRegistration() {
  return async (dispatch: Dispatch) => {
    const productRegistration = localStorage.getItem<ProductRegistrationInfo>(
      'productRegistration'
    );
    if (productRegistration) {
      dispatch(setProductRegistrationInfo(productRegistration));
      dispatch(completeProductRegistrationStart());
    }
  };
}

export function productRegistrationError(error: ApiError) {
  return async (dispatch: Dispatch) => {
    // Handle error
    switch (error.code) {
      case 'relationship.my-products.exists':
      case 'relationship.sold-as-new-to':
        dispatch(completeProductRegistrationSuccess());
        break;
      default:
        dispatch(completeProductRegistrationFailure());
    }
  };
}
